import { FormControl, RadioGroup } from '@mui/material';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import { useOktaAuth } from '@okta/okta-react';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from "react-router-dom";
import { format } from 'react-string-format';
import { constants } from '../../constants';
import useMyMApi from "../../hooks/useMyMApi";
import useValidationRules from '../../hooks/useValidationRules';
import useWebContext from '../../hooks/useWebContext';
import { MyMButton } from '../../ui-elements/MyMButton';
import { MyMDropDownList } from '../../ui-elements/MyMDropDownList';
import { MyMLabel } from '../../ui-elements/MyMLabel';
import { MyMProgress } from '../../ui-elements/MyMProgress';
import { MyMRadio } from '../../ui-elements/MyMRadio';
import { MyMTextBox } from '../../ui-elements/MyMTextBox';
import { NavigateLink } from '../../ui-parts/NavigateLink';
import ValidatableTextBox from '../../ui-parts/ValidatableTextBox';
import { utility } from '../../utility';
import { MyMTooltip } from '../../ui-elements/MyMToolTip';
import { mediaQuery, useMediaQuery } from '../../hooks/useMediaQuery';
import { IconButton } from '@mui/material';
import Box from '@mui/material/Box';
import PendingIcon from '@mui/icons-material/Pending';
import ClickAwayListener from '@mui/material/ClickAwayListener';


/**
 * NewUserRegistration Page Component
 */
const NewUserRegistration = () => {
  // 変数・State
  const screenId = 'MyM201';
  const { controlSettings, messageList, screenSettings } = useWebContext(screenId);
  const apiRequest = useMyMApi(screenId);
  const navigate = useNavigate();
  const validationRules = useValidationRules();
  const { handleSubmit, control, formState, trigger, clearErrors, setValue } = useForm({
    mode: 'onSubmit',
  })

  const [companySelected, setCompanySelected] = useState(null);
  const [rhoList, setRhoList] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [selectSupportedRhoCode, setSelectSupportedRhoCode] = useState(null);
  const [rhoMailAddressWithoutDomain, setRhoMailAddressWithoutDomain] = useState('');
  const supporedRhoDomainRef = useRef('');
  const [rejectedReason, setRejectedReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { authState, oktaAuth } = useOktaAuth();
  const isPc = useMediaQuery(mediaQuery.pc);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  // ページロード時処理
  useEffect(() => {
    if (authState.isAuthenticated === false && !sessionStorage.getItem(constants.HttpStorageItemKey.DebugUserSsoKey)) {
      navigate('/MyM999', { state: { message: constants.Message.InvalidRequest } });
      return;
    }


    // APIコールバック処理
    const callbackPageLoadContext = async (result) => {
      setRhoList(result.RhoList);

      // 新規ユーザの場合、Oktaのユーザ情報を規定値として設定
      if (result.UserInfo.StatusId === -1) {
        await oktaAuth.token.getUserInfo().then(e => {
          result.UserInfo.UserId = e.email;
          result.UserInfo.FirstName = e.given_name;
          result.UserInfo.LastName = e.family_name;
          result.UserInfo.CompanyName = e.Companyname;
          result.UserInfo.CompanyCategory = constants.CompanyCategory.HCM;
        });
      }
      setUserInfo(result.UserInfo); // DBから取得したユーザ情報をStateに設定する
      setCompanySelected(result.UserInfo.CompanyCategory);

      // ユーザ情報から会社情報を設定
      let domain = "";
      let selectedCompanyInfo = {};
      if (result.UserInfo.RhqContact != null) {
        domain = result.UserInfo.RhqContact.substring(result.UserInfo.RhqContact.indexOf(controlSettings.LabelList.lblSupportedCompanyAt) + 1);
        selectedCompanyInfo = result.RhoList.find((x) => { return x.RhoMailDomain === domain ? x.RhoCode : "" });
      }
      setSelectSupportedRhoCode(selectedCompanyInfo?.RhoCode);
      setRhoMailAddressWithoutDomain(result.UserInfo.RhqContact?.substring(0, result.UserInfo.RhqContact.indexOf(controlSettings.LabelList.lblSupportedCompanyAt)) ?? '');
      supporedRhoDomainRef.current = domain ?? '';

      setRejectedReason(result.RejectedReason);
    }
    // UserInfoController.csのAPI呼び出し
    apiRequest.requestJson('api/user-info/load-new', 'GET', callbackPageLoadContext)
  }, []);

  // ラジオボタン変更時処理
  const onChangeCompany = (event) => {
    // ラジオボタンの入力値State更新、SupportedByのInput値のリセット
    setCompanySelected(() => event.target.value);

    // supportedRhoクリア
    setSelectSupportedRhoCode(null);
    // メールアドレスクリア
    setRhoMailAddressWithoutDomain('');
    // メールアドレスドメインクリア
    supporedRhoDomainRef.current = '';

    setValue("rhoMailAddressWithoutDomain", '');
    clearErrors("rhoMailAddressWithoutDomain");
  };

  // ドロップダウン変更時処理
  const onChangeRhoMailAddressWithoutDomain = (event) => {
    const optionData = rhoList.find(item => { return event.target.value === item.RhoCode });

    setSelectSupportedRhoCode(optionData?.RhoCode);
    supporedRhoDomainRef.current = optionData?.RhoMailDomain ?? '';

    // メールアドレスチェックトリガー起動
    if (formState.isSubmitted) {
      trigger("rhoMailAddressWithoutDomain");
    }
  };

  // Top画面へ戻る
  const navigateTop = () => {
    utility.transferTopPage();
  }

  // Requestボタン押下時処理
  const onClickRequest = (form) => {

    if (window.confirm(messageList.MyM_MSG_I_0074)) {
      // Requestパラメータ作成
      const param = {
        UserSequenceNo: userInfo.UserSequenceNo === 0 ? null : userInfo.UserSequenceNo,
        UserId: userInfo.UserId,
        FirstName: userInfo.FirstName,
        LastName: userInfo.LastName,
        CompanyName: userInfo.CompanyName,
        CompanyCategory: companySelected,
        StatusId: constants.UserStatus.Requesting,
        // RHQContactメールアドレス作成
        // DealerOperationが選択されていても、入力されたアドレスが不完全ならNULLとして送る
        RhqContact: companySelected === constants.CompanyCategory.DealerOperation && form.rhoMailAddressWithoutDomain !== "" && supporedRhoDomainRef.current !== ""
          ? form.rhoMailAddressWithoutDomain + controlSettings.LabelList.lblSupportedCompanyAt + supporedRhoDomainRef.current
          : null,
        RoleId: userInfo.RoleId,
        CompanyId: userInfo.CompanyId,
      }

      // 正常終了時APIコールバック処理
      const callbackResistrateRequestContext = (result) => {
        if (!result.PostResult) {
          navigate('/MYM703', { state: { result } });
        }

        else {
          alert(messageList.MyM_MSG_I_0028); // 処理完了メッセージ
          navigateTop();
          setIsLoading(false);
        }
        
      }

      // 失敗時APIコールバック処理
      const callbackResistrateRequestFailed = () => {
        setIsLoading(false);
      }

      //ユーザ情報登録処理呼び出し
      setIsLoading(true);
      apiRequest.requestJson('api/user-info/update-for-newuser', 'POST', callbackResistrateRequestContext, callbackResistrateRequestFailed, param);
    }
  };


 

  const onError = (errors, e) => {
    window.alert(messageList.MyM_MSG_E_0003);
  };

  const handleTooltipToggle = () => {
    setTooltipOpen(!tooltipOpen);
  };

  const handleTooltipClose = () => {
    setTooltipOpen(false);
  };

  if (userInfo === null)
    return <MyMProgress />

  return (
    <Grid container sx={{ margin: isPc ? '2vw 33vw 0' : '', padding: isPc ? '1vw' : '10px', width: isPc ? '35%' : '100%', textAlign: 'left', minWidth: 250 }}>
      {isLoading && <MyMProgress />}
      <Grid item xs={12}>
        {userInfo.StatusId === constants.UserStatus.Rejected &&
          <MyMLabel sx={{ color: 'red' }}>{controlSettings.LabelList.lblNotificationMessage}{rejectedReason}</MyMLabel>
        }
        <MyMLabel className='fw-bold mt-4'>{controlSettings.LabelList.lblUserIDTitle}</MyMLabel>
        <ValidatableTextBox
          control={control}
          name="userId"
          defaultValue={userInfo.UserId}
          formState={formState}
          maxLength={256}
          onBlur={e => userInfo.UserId = e.target.value}
          rules={{
            ...validationRules.required(),
            ...validationRules.maxLength(256),
            ...validationRules.mailAddress(),
          }}
        />
      </Grid>
      <Grid item xs={12} sm={6} sx={{ paddingRight: '4px' }}>
        <MyMLabel className='fw-bold mt-4'>{controlSettings.LabelList.lblFirstName}</MyMLabel>
        <MyMTextBox fullWidth value={userInfo.FirstName} disabled={true}></MyMTextBox>
      </Grid>
      <Grid item xs={12} sm={6} sx={{ paddingLeft: '4px' }}>
        <MyMLabel className='fw-bold mt-4'>{controlSettings.LabelList.lblLastName}</MyMLabel>
        <MyMTextBox fullWidth value={userInfo.LastName} disabled={true}></MyMTextBox>
      </Grid>
      <Grid item xs={12}>
        <Box display="flex" alignItems="center">
          <MyMLabel className='fw-bold mt-4'>{controlSettings.LabelList.lblCompanyTitle}</MyMLabel>
          {!isPc && (
            <ClickAwayListener onClickAway={handleTooltipClose}>
              <div>
                <MyMTooltip
                  PopperProps={{
                    disablePortal: true,
                  }}
                  onClose={handleTooltipClose}
                  open={tooltipOpen}
                  disableFocusListener
                  disableHoverListener
                  disableTouchListener
                  title={controlSettings.LabelList.lblCompanyToolTip}
                >
                  <Stack direction="row" alignItems="center">
                    <IconButton
                      onClick={handleTooltipToggle}
                      sx={{ marginTop: '23px' }}  
                    >
                      <PendingIcon />
                    </IconButton>
                  </Stack>
                </MyMTooltip>
              </div>
            </ClickAwayListener>

          )}
        </Box>
        <ValidatableTextBox
          control={control}
          name="CompanyName"
          formState={formState}
          fullWidth
          defaultValue={userInfo.CompanyName}
          onBlur={e => userInfo.CompanyName = e.target.value}
          rules={{
            ...validationRules.required(),
            ...validationRules.maxLength(64)
          }}
        />
      </Grid>

      <Grid item xs={12} sx={{ borderTop: '1px solid #000', marginTop: '1vw', width: "100%" }}>
        <FormControl>
          <RadioGroup name="companyRadioGroup" defaultValue={userInfo.CompanyCategory} value={companySelected} onChange={onChangeCompany} >
            <MyMRadio value={constants.CompanyCategory.HCM} label={controlSettings.LabelList.lblCompany1kinds} />
            <MyMRadio value={constants.CompanyCategory.HitachiRegionalHeadOffice} label={controlSettings.LabelList.lblCompany2kinds} />
            <MyMRadio value={constants.CompanyCategory.DealerOperation} label={controlSettings.LabelList.lblCompany3kinds} />
            <FormControl sx={{ marginLeft: '2vw' }}>
              <MyMLabel>{controlSettings.LabelList.lblSupportedBy}</MyMLabel>
              <MyMDropDownList
                name="supportedBy"
                sx={{ width: '150px' }}
                value={selectSupportedRhoCode ?? ''}
                options={rhoList.map((list) => {
                  return {
                    value: list.RhoCode,
                    text: list.RhoName,
                  }
                })}
                needsBlank={true}
                onChange={(e) => { onChangeRhoMailAddressWithoutDomain(e); }}
                disabled={companySelected !== constants.CompanyCategory.DealerOperation}
              />
              <Grid container spacing={2} className="mt-0 pb-2" sx={{ verticalAlign: "middle", width: "36vw", height: 85, minWidth: 240 }}>
                <Grid item xs={12} sm={6} md={6} lg={6} sx={{ minWidth: 240 }}>
                  <ValidatableTextBox
                    control={control}
                    name="rhoMailAddressWithoutDomain"
                    defaultValue={rhoMailAddressWithoutDomain}
                    formState={formState}
                    maxLength={256 - (controlSettings.LabelList.lblSupportedCompanyAt.length + supporedRhoDomainRef.current.length)}
                    onchange={e => setRhoMailAddressWithoutDomain(e.target.value)}
                    disabled={!(companySelected === constants.CompanyCategory.DealerOperation)}
                    rules={
                      companySelected === constants.CompanyCategory.DealerOperation &&
                      {
                        ...{
                          validate: {
                            message: (value) => {
                              if ((value !== '' && supporedRhoDomainRef.current === '') || (value === '' && supporedRhoDomainRef.current !== '')) {
                                return messageList.MyM_MSG_E_0024;
                              }
                              return;
                            }
                          }
                        },
                        ...validationRules.maxLength(256 - (controlSettings.LabelList.lblSupportedCompanyAt.length + supporedRhoDomainRef.current.length)),
                        ...validationRules.mailAddressWithoutDomain(),
                      }
                    }
                    InputProps={{
                      endAdornment:
                        <InputAdornment position='end'>
                          {`${controlSettings.LabelList.lblSupportedCompanyAt}${supporedRhoDomainRef.current}`}
                        </InputAdornment>
                    }}
                  />
                </Grid>
              </Grid>
            </FormControl>
            <MyMRadio value={constants.CompanyCategory.SubsidiaryCompany} label={controlSettings.LabelList.lblCompany4kinds} />
          </RadioGroup>
        </FormControl>
      </Grid>
      <MyMLabel sx={{ margin: '1vw 0 0.5vw 0', fontSize: '0.95rem' }}>
        {format(controlSettings.LabelList.lblReadTermsOfUse,
          <NavigateLink newTabNavigate={true} to='/MyM904' sx={{ fontSize: '0.95rem' }}>
            {controlSettings.LabelList.lnkTermsOfUse}
          </NavigateLink>)}
      </MyMLabel>
      <MyMLabel sx={{ mb: '1.5vw', fontSize: '0.95rem' }}>
        {format(controlSettings.LabelList.lblAccountSupport,
          <Link href={`mailto:${screenSettings.SupportEmailTo}`} >
            {controlSettings.LabelList.lnkAccountSupport}
          </Link>)}
      </MyMLabel>
      <Grid item xs={12} className='d-flex justify-content-end'>
        <Stack direction='row' spacing={3}>
          <MyMButton onClick={handleSubmit(onClickRequest, onError)}>{controlSettings.LabelList.btnRequest}</MyMButton>
          <MyMButton onClick={navigateTop}>{controlSettings.LabelList.btnCancel}</MyMButton>
        </Stack>
      </Grid>
    </Grid>
  );
}

export default NewUserRegistration;

